import React, { useCallback, useEffect, useRef } from 'react';
import { ShapesDrawingToolsDndIcon } from '@icons';

import './AnnotationShortcutHint.less';

const POSITION_KEY = 'annotation-shortcut-hint-position';

function AnnotationShortcutHint() {
  const containerRef = useRef<HTMLDivElement>(null);
  const isMouseDownRef = useRef(false);
  const dragOffsetRef = useRef({ left: 0, top: 0 });

  const clampTranslate = useCallback((x: number, y: number) => {
    const el = containerRef.current;
    if (!el) return { x, y };

    const videoBounds = document
      .querySelector('.video-js')
      ?.getBoundingClientRect();
    if (!videoBounds) return { x, y };

    const { left, top } = dragOffsetRef.current;
    const width = el.offsetWidth;
    const height = el.offsetHeight;

    return {
      x: Math.min(
        Math.max(x, videoBounds.left - left),
        videoBounds.right - width - left
      ),
      y: Math.min(
        Math.max(y, videoBounds.top - top),
        videoBounds.bottom - height - top
      )
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return;
    try {
      const { left, top, transform } = JSON.parse(raw);
      el.style.position = 'fixed';
      el.style.top = top;
      el.style.left = left;
      el.style.right = 'unset';
      el.style.bottom = 'unset';
      el.style.transform = transform;
      el.style.zIndex = '1000';
    } catch {
      // eslint-disable-next-line no-empty
    }
  }, []);

  useEffect(() => {
    const callback = (e: MouseEvent) => {
      if (!isMouseDownRef.current || !containerRef.current) return;
      const skip = e.pageX === 0 && e.pageY === 0;
      if (!skip) {
        const { x, y } = clampTranslate(e.clientX, e.clientY);
        containerRef.current.style.transform = `translate(${x}px, ${y}px)`;
      }
    };
    document.addEventListener('mousemove', callback);
    return () => {
      document.removeEventListener('mousemove', callback);
    };
  }, [clampTranslate]);

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!containerRef.current) return;
      isMouseDownRef.current = true;
      const rect = containerRef.current.getBoundingClientRect();
      const left = rect.x - e.clientX;
      const top = rect.y - e.clientY;
      dragOffsetRef.current = { left, top };
      containerRef.current.style.position = 'fixed';
      containerRef.current.style.top = `${top}px`;
      containerRef.current.style.right = 'unset';
      containerRef.current.style.left = `${left}px`;
      containerRef.current.style.bottom = 'unset';
      const { x, y } = clampTranslate(e.clientX, e.clientY);
      containerRef.current.style.transform = `translate(${x}px, ${y}px)`;
      containerRef.current.style.zIndex = '1000';
    },
    [clampTranslate]
  );

  const onMouseUp = useCallback(() => {
    isMouseDownRef.current = false;
    const el = containerRef.current;
    if (!el?.style.transform) return;
    localStorage.setItem(
      POSITION_KEY,
      JSON.stringify({
        left: el.style.left,
        top: el.style.top,
        transform: el.style.transform
      })
    );
  }, []);

  return (
    <div
      ref={containerRef}
      className="annotation_shortcut_hint"
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
    >
      <div className="annotation_shortcut_hint__dragger">
        <ShapesDrawingToolsDndIcon />
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">Space</div> Play/Pause{' '}
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">D</div> Annotate
      </div>
      <div className="annotation_shortcut_hint__text">
        <div className="annotation_shortcut_hint__key">C</div> Comment
      </div>
    </div>
  );
}

export default AnnotationShortcutHint;
